import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createWorkOrder, updateWorkOrder, getWorkOrder, getUsers, getStaff } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, AlertCircle } from 'lucide-react';

const WorkOrderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useLanguage();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [previewNumber, setPreviewNumber] = useState('');
  const [formData, setFormData] = useState({
    farm: 'LP',
    title: '',
    description: '',
    work_type: 'other',
    priority: 'medium',
    section_code: '',
    contractor_id: '',
    contractor_name: '',
    assigned_staff_id: '',
    assigned_staff_name: '',
    deadline: '',
    estimated_days: '',
    cost_type: 'per_task',
    quantity: '',
    unit_cost: '',
    estimated_cost: '',
    currency: 'USD',
    payment_term_days: '',
    payment_term_comment: '',
    materials_farm_provides: '',
    materials_contractor_brings: '',
  });

  const isTaskType = formData.work_type === 'task';

  useEffect(() => {
    loadContractors();
    loadStaff();
    if (isEditing) {
      loadWorkOrder();
    } else {
      // Load preview for new work orders
      loadPreviewNumber(formData.farm);
    }
  }, [id]);

  // Load preview number when farm changes
  useEffect(() => {
    if (!isEditing && formData.farm) {
      loadPreviewNumber(formData.farm);
    }
  }, [formData.farm, isEditing]);

  const loadPreviewNumber = async (farm) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/work-orders/preview-number/${farm}`);
      setPreviewNumber(response.data.work_order_number);
    } catch (error) {
      console.error('Failed to load preview number:', error);
    }
  };

  const loadContractors = async () => {
    try {
      const users = await getUsers();
      setContractors(users.filter((u) => u.role === 'contractor'));
    } catch (error) {
      toast.error('Failed to load contractors');
    }
  };

  const loadStaff = async () => {
    try {
      const staff = await getStaff();
      setStaffList(staff);
    } catch (error) {
      toast.error('Failed to load staff');
    }
  };

  const loadWorkOrder = async () => {
    try {
      const data = await getWorkOrder(id);
      setFormData({
        farm: data.farm || 'LP',
        title: data.title,
        description: data.description,
        work_type: data.work_type,
        priority: data.priority,
        section_code: data.section_code,
        contractor_id: data.contractor_id || '',
        contractor_name: data.contractor_name || '',
        assigned_staff_id: data.assigned_staff_id || '',
        assigned_staff_name: data.assigned_staff_name || '',
        deadline: data.deadline ? new Date(data.deadline).toISOString().split('T')[0] : '',
        estimated_days: data.estimated_days || '',
        cost_type: data.cost_type || 'per_task',
        quantity: data.quantity || '',
        unit_cost: data.unit_cost || '',
        estimated_cost: data.estimated_cost || '',
        currency: data.currency || 'USD',
        payment_term_days: data.payment_term_days || '',
        payment_term_comment: data.payment_term_comment || '',
        materials_farm_provides: data.materials_farm_provides || '',
        materials_contractor_brings: data.materials_contractor_brings || '',
      });
    } catch (error) {
      toast.error('Failed to load work order');
      navigate('/work-orders');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : null,
        estimated_days: formData.estimated_days ? parseInt(formData.estimated_days) : null,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        // For Task type, no labor cost - only material costs allowed
        estimated_cost: isTaskType ? (formData.estimated_cost ? parseFloat(formData.estimated_cost) : null) : (formData.estimated_cost ? parseFloat(formData.estimated_cost) : null),
        payment_term_days: formData.payment_term_days ? parseInt(formData.payment_term_days) : null,
        // Clear contractor fields for Task type
        contractor_id: isTaskType ? null : (formData.contractor_id || null),
        contractor_name: isTaskType ? null : (formData.contractor_name || null),
        // Clear staff fields for non-Task type
        assigned_staff_id: isTaskType ? (formData.assigned_staff_id || null) : null,
        assigned_staff_name: isTaskType ? (formData.assigned_staff_name || null) : null,
      };

      if (isEditing) {
        await updateWorkOrder(id, payload);
        toast.success('Work order updated successfully');
      } else {
        const created = await createWorkOrder(payload);
        toast.success(`Work order ${created.work_order_number} created successfully`);
        navigate(`/work-orders/${created.id}`);
        return;
      }
      navigate(`/work-orders/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save work order');
    } finally {
      setLoading(false);
    }
  };

  const handleContractorChange = (userId) => {
    if (userId === 'unassigned') {
      setFormData({
        ...formData,
        contractor_id: '',
        contractor_name: '',
      });
    } else {
      const contractor = contractors.find((c) => c.id === userId);
      setFormData({
        ...formData,
        contractor_id: userId,
        contractor_name: contractor ? contractor.full_name : '',
      });
    }
  };

  const handleStaffChange = (staffId) => {
    if (staffId === 'unassigned') {
      setFormData({
        ...formData,
        assigned_staff_id: '',
        assigned_staff_name: '',
      });
    } else {
      const staff = staffList.find((s) => s.id === staffId);
      setFormData({
        ...formData,
        assigned_staff_id: staffId,
        assigned_staff_name: staff ? staff.name : '',
      });
    }
  };

  const handleWorkTypeChange = (value) => {
    setFormData({
      ...formData,
      work_type: value,
      // Reset assignments when switching type
      contractor_id: value === 'task' ? '' : formData.contractor_id,
      contractor_name: value === 'task' ? '' : formData.contractor_name,
      assigned_staff_id: value !== 'task' ? '' : formData.assigned_staff_id,
      assigned_staff_name: value !== 'task' ? '' : formData.assigned_staff_name,
      // Reset labor cost fields for Task type
      unit_cost: value === 'task' ? '' : formData.unit_cost,
      quantity: value === 'task' ? '' : formData.quantity,
    });
  };

  // Calculate total cost in real-time
  const calculatedCost = formData.quantity && formData.unit_cost
    ? (parseFloat(formData.quantity) * parseFloat(formData.unit_cost)).toFixed(2)
    : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="work-order-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/work-orders')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-4xl font-bold font-heading text-foreground">
            {isEditing ? t('edit') : t('newWorkOrder')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isEditing ? 'Update work order details' : 'Create a new work order'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{t('workOrderDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Farm Selection */}
              <div className="space-y-2">
                <Label htmlFor="farm">{t('farm')} *</Label>
                <Select
                  value={formData.farm}
                  onValueChange={(value) => setFormData({ ...formData, farm: value })}
                  disabled={isEditing}
                >
                  <SelectTrigger id="farm" data-testid="farm-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LP">La Punta (LP)</SelectItem>
                    <SelectItem value="RA">Rancagua (RA)</SelectItem>
                  </SelectContent>
                </Select>
                {!isEditing && previewNumber && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Work Order #: <span className="font-semibold text-primary">{previewNumber}</span>
                  </p>
                )}
              </div>

              {/* Section Code */}
              <div className="space-y-2">
                <Label htmlFor="section_code">{t('sectionCode')} *</Label>
                <Input
                  id="section_code"
                  data-testid="section-code-input"
                  value={formData.section_code}
                  onChange={(e) => setFormData({ ...formData, section_code: e.target.value })}
                  required
                  placeholder="e.g., A-1, B-3"
                />
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label htmlFor="priority">{t('priority')} *</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger id="priority" data-testid="priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title & Description */}
            <div className="space-y-2">
              <Label htmlFor="title">{t('title')} *</Label>
              <Input
                id="title"
                data-testid="title-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Enter work order title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('description')} *</Label>
              <Textarea
                id="description"
                data-testid="description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={4}
                placeholder="Detailed description of the work"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Work Type */}
              <div className="space-y-2">
                <Label htmlFor="work_type">{t('workType')} *</Label>
                <Select
                  value={formData.work_type}
                  onValueChange={handleWorkTypeChange}
                >
                  <SelectTrigger id="work_type" data-testid="work-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">{t('task')} ({t('internalStaff')})</SelectItem>
                    <SelectItem value="architectural">{t('architectural')}</SelectItem>
                    <SelectItem value="pruning">{t('pruning')}</SelectItem>
                    <SelectItem value="harvesting">{t('harvesting')}</SelectItem>
                    <SelectItem value="irrigation">{t('irrigation')}</SelectItem>
                    <SelectItem value="pest_control">{t('pestControl')}</SelectItem>
                    <SelectItem value="fertilization">{t('fertilization')}</SelectItem>
                    <SelectItem value="equipment_maintenance">{t('equipmentMaintenance')}</SelectItem>
                    <SelectItem value="other">{t('other')}</SelectItem>
                  </SelectContent>
                </Select>
                {isTaskType && (
                  <p className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                    <AlertCircle className="h-3 w-3" />
                    {t('taskTypeNote')}
                  </p>
                )}
              </div>

              {/* Contractor OR Staff Assignment */}
              {isTaskType ? (
                <div className="space-y-2">
                  <Label htmlFor="staff">{t('assignedStaff')} *</Label>
                  <Select value={formData.assigned_staff_id || 'unassigned'} onValueChange={handleStaffChange}>
                    <SelectTrigger id="staff" data-testid="staff-select">
                      <SelectValue placeholder={t('selectStaff')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">{t('unassigned')}</SelectItem>
                      {staffList.map((staff) => (
                        <SelectItem key={staff.id} value={staff.id}>
                          {staff.name} - {staff.position}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {staffList.length === 0 && (
                    <p className="text-xs text-amber-600">
                      {t('noStaffMembers')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="contractor">{t('contractor')}</Label>
                  <Select value={formData.contractor_id || 'unassigned'} onValueChange={handleContractorChange}>
                    <SelectTrigger id="contractor" data-testid="contractor-select">
                      <SelectValue placeholder="Select a contractor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">{t('unassigned')}</SelectItem>
                      {contractors.map((contractor) => (
                        <SelectItem key={contractor.id} value={contractor.id}>
                          {contractor.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Deadline */}
              <div className="space-y-2">
                <Label htmlFor="deadline">{t('deadline')}</Label>
                <Input
                  id="deadline"
                  data-testid="deadline-input"
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                />
              </div>

              {/* Estimated Days */}
              <div className="space-y-2">
                <Label htmlFor="estimated_days">{t('estimatedDays')}</Label>
                <Input
                  id="estimated_days"
                  data-testid="estimated-days-input"
                  type="number"
                  min="1"
                  value={formData.estimated_days}
                  onChange={(e) => setFormData({ ...formData, estimated_days: e.target.value })}
                  placeholder="Days"
                />
              </div>
            </div>

            {/* Cost Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">
                {isTaskType ? t('materialCostOnly') : t('costInformation')}
              </h3>
              {isTaskType && (
                <p className="text-sm text-muted-foreground mb-4">
                  {t('taskNoCostNote')}
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Cost Type - hidden for Tasks */}
                {!isTaskType && (
                  <div className="space-y-2">
                    <Label htmlFor="cost_type">{t('costType')}</Label>
                    <Select
                      value={formData.cost_type}
                      onValueChange={(value) => setFormData({ ...formData, cost_type: value })}
                    >
                      <SelectTrigger id="cost_type" data-testid="cost-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_task">{t('perTask')}</SelectItem>
                        <SelectItem value="per_tree">{t('perTree')}</SelectItem>
                        <SelectItem value="per_hectare">{t('perHectare')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Quantity - hidden for Tasks */}
                {!isTaskType && formData.cost_type !== 'per_task' && (
                  <div className="space-y-2">
                    <Label htmlFor="quantity">
                      {t('quantity')} ({formData.cost_type === 'per_tree' ? 'Trees' : 'Hectares'})
                    </Label>
                    <Input
                      id="quantity"
                      data-testid="quantity-input"
                      type="number"
                      step="0.01"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                )}

                {/* Unit Cost - hidden for Tasks */}
                {!isTaskType && formData.cost_type !== 'per_task' && (
                  <div className="space-y-2">
                    <Label htmlFor="unit_cost">{t('unitCost')}</Label>
                    <Input
                      id="unit_cost"
                      data-testid="unit-cost-input"
                      type="number"
                      step="0.01"
                      value={formData.unit_cost}
                      onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                )}

                {/* Total/Estimated Cost - for Tasks it's material cost only */}
                <div className="space-y-2">
                  <Label htmlFor="estimated_cost">
                    {isTaskType ? t('materialCost') : t('estimatedCost')}
                  </Label>
                  <Input
                    id="estimated_cost"
                    data-testid="estimated-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    placeholder={calculatedCost || "0.00"}
                  />
                  {!isTaskType && calculatedCost && (
                    <p className="text-xs text-muted-foreground">
                      Auto-calculated: <span className="font-semibold text-primary">
                        {formData.currency} {calculatedCost}
                      </span>
                    </p>
                  )}
                </div>

                {/* Currency */}
                <div className="space-y-2">
                  <Label htmlFor="currency">{t('currency')}</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger id="currency" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="CLP">CLP ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Materials Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Materials</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="materials_farm_provides">{t('materialsFarmProvides')}</Label>
                  <Textarea
                    id="materials_farm_provides"
                    data-testid="materials-farm-input"
                    value={formData.materials_farm_provides}
                    onChange={(e) =>
                      setFormData({ ...formData, materials_farm_provides: e.target.value })
                    }
                    rows={4}
                    placeholder="List materials the farm will provide..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="materials_contractor_brings">
                    {t('materialsContractorBrings')}
                  </Label>
                  <Textarea
                    id="materials_contractor_brings"
                    data-testid="materials-contractor-input"
                    value={formData.materials_contractor_brings}
                    onChange={(e) =>
                      setFormData({ ...formData, materials_contractor_brings: e.target.value })
                    }
                    rows={4}
                    placeholder="List materials the contractor needs to bring..."
                  />
                </div>
              </div>
            </div>

            {/* Payment Terms Section - hide for Task type (internal staff) */}
            {!isTaskType && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Payment Terms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="payment_term_days">Payment Terms (after work completion)</Label>
                    <Select
                      value={formData.payment_term_days ? formData.payment_term_days.toString() : 'none'}
                      onValueChange={(value) => setFormData({ ...formData, payment_term_days: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger id="payment_term_days" data-testid="payment-term-select">
                        <SelectValue placeholder="Select payment terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="payment_term_comment">Payment Terms Comment (Optional)</Label>
                    <Textarea
                      id="payment_term_comment"
                      data-testid="payment-term-comment-input"
                      value={formData.payment_term_comment}
                      onChange={(e) =>
                        setFormData({ ...formData, payment_term_comment: e.target.value })
                      }
                      rows={3}
                      placeholder="Any additional payment terms or conditions..."
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/work-orders')}
                data-testid="cancel-button"
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-button">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    {isEditing ? 'Update' : t('create')} Work Order
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default WorkOrderForm;
